import { LayoutOutlined, BgColorsOutlined, FileTextOutlined, PictureOutlined, NumberOutlined } from "@ant-design/icons";
import { ReactNode, useState, useEffect } from "react";
import { BackgroundComponent } from "./BackgroundConfig/BackgroundConfig";
import { CaptionConfig } from "./CaptionConfig/CaptionConfig";
import { ColorComponent } from "./ColorConfig/ColorConfig";
import { FrameComponent } from "./FrameConfig/FrameConfig";
import { LayoutComponent } from "./LayoutConfig/LayoutConfig";
import { ThemeConfig } from "./ThemeConfig/ThemeConfig";
import { LogoComponent } from "./LogoConfig/LogoConfig";
import { Template } from "../VideoEditor";
import { ThemeIcon } from "../../../common/Icons/ThemeIcon";
import sampleVideoData from '../../remotion/video_data_to_render.json';
import { LogoIcon } from "./LogoIcon";
import TrebbleSegmented, { TrebbleSegmentedOnChangeValue } from "../../../common/TrebbleSegmented";
import ti18n from 'i18n!nls/ClipCreatori18n';

// Extend Window interface if not already done globally
declare global {
    interface Window {
      getI18n: (i18nData: any, key: string, options?: any) => string;
    }
  }

export const getEnumFromValue = (value: any, ENUMS: Readonly<{ SQUARE: { value: string; label: string; suitable_platforms: string[]; }; PORTRAIT: { value: string; label: string; suitable_platforms: string[]; }; LANDSCAPE: { value: string; label: string; suitable_platforms: string[]; }; }> | Readonly<{ TREBBLE: { label: string; value: string; mainColor: string; complementaryColor: string; }; OCEAN: { label: string; value: string; mainColor: string; complementaryColor: string; }; PURPLE: { label: string; value: string; mainColor: string; complementaryColor: string; }; FOREST: { label: string; value: string; mainColor: string; complementaryColor: string; }; CHERRY: { label: string; value: string; mainColor: string; complementaryColor: string; }; CUSTOM: { label: string; value: string; mainColor: string; complementaryColor: string; }; }> | Readonly<{ DEFAULT: { value: string; label: string; }; SPLIT: { value: string; label: string; }; SCREENSHARE: { value: string; label: string; }; GAMEPLAY: { value: string; label: string; }; THREE: { value: string; label: string; }; FOUR: { value: string; label: string; }; }> | { [s: string]: unknown; } | ArrayLike<unknown>) => {
    return Object.values(ENUMS).find((e: any) => e.value === value);
};

const createNavigationMenu = (label: ReactNode, value: string, icon: ReactNode) => {
    return {
        labelString: label,
        value,
        label: (
            <div style={{}} className="cursor-pointer py-1">
                {icon}
                <div className="text-xs">{label}</div>
            </div>
        ),
    };
};

export const TEMPLATE_NAVIGATION_MENUS = Object.freeze({
    THEME: createNavigationMenu(window.getI18n(ti18n, "THEME"), "theme", <ThemeIcon className="h-1em m-auto"/>),
    LAYOUT: createNavigationMenu(window.getI18n(ti18n, "LAYOUT"), "layout", <LayoutOutlined />),
    /*COLORS: createNavigationMenu("Colors", "colors", <BgColorsOutlined />),*/
    CAPTIONS: createNavigationMenu(window.getI18n(ti18n, "CAPTIONS"), "captions", <FileTextOutlined />),
    /*BACKGROUND: createNavigationMenu(
        <div className="mb-2">
            <div style={{ lineHeight: "12px" }}>Back-</div>
            <div style={{ lineHeight: "12px" }}>ground</div>
        </div>,
        "background",
        <PictureOutlined />
    ),*/
    FRAME: createNavigationMenu(window.getI18n(ti18n, "FRAME"), "frame", <NumberOutlined />),
    LOGO: createNavigationMenu(window.getI18n(ti18n, "LOGO"), "logo", <span role="img" aria-label="number" className="anticon anticon-number"><LogoIcon /></span>),
});

export const TemplateNavigationComponent = ({ 
    selectedTemplate, 
    setSelectedTemplate, 
    videoRenderData, 
    setIsConfigEditing,
    workspaceId
}: { 
    selectedTemplate: Template, 
    setSelectedTemplate: (template: Template) => void, 
    videoRenderData: typeof sampleVideoData,
    setIsConfigEditing: (isEditing: boolean) => void,
    workspaceId: string | null | undefined
}) => {
    const [viewSelected, setViewSelected] = useState<string>(
        TEMPLATE_NAVIGATION_MENUS.THEME.value
    );

    // Log initial values
    useEffect(() => {
        console.log('TemplateNavigationComponent loaded with values:', {
            showCaption: selectedTemplate.showCaption,
            overrideThemeCaption: selectedTemplate.overrideThemeCaption,
            selectedTemplateId: selectedTemplate.templateID
        });
    }, []);

    return (
        <div className="flex flex-row h-[520px]" >
            <div style={{ width: "75px" }} className="py-2">
                <TrebbleSegmented
                    vertical={true}
                    value={viewSelected}
                    defaultValue={TEMPLATE_NAVIGATION_MENUS.THEME.value}
                    onChange={(value: TrebbleSegmentedOnChangeValue) => setViewSelected(value as string)}
                    className="m-0 p-0"
                    block={true}
                    options={Object.values(TEMPLATE_NAVIGATION_MENUS)}
                />
            </div>
            <div className="flex flex-row w-full overflow-auto pl-2">
                <div className="w-full ">
                    {viewSelected === TEMPLATE_NAVIGATION_MENUS.LAYOUT.value && (
                        <LayoutComponent 
                            selectedTemplate={selectedTemplate} 
                            setSelectedTemplate={setSelectedTemplate} 
                            videoRenderData={videoRenderData}
                            inCloudRenderingEnvironment={false} 
                        />
                    )}
                    {viewSelected === TEMPLATE_NAVIGATION_MENUS.THEME.value && (
                        <ThemeConfig
                            template={selectedTemplate}
                            onTemplateChange={setSelectedTemplate}
                            onEditingChange={setIsConfigEditing}
                            workspaceId={workspaceId}
                        />
                    )}
                    {/* viewSelected === TEMPLATE_NAVIGATION_MENUS.COLORS.value && (
                        <ColorComponent selectedTemplate={selectedTemplate} setSelectedTemplate={setSelectedTemplate} />
                    )*/}
                    {viewSelected === TEMPLATE_NAVIGATION_MENUS.CAPTIONS.value && (
                        <CaptionConfig
                            template={selectedTemplate}
                            onTemplateChange={setSelectedTemplate}
                            onEditingChange={setIsConfigEditing}
                            workspaceId={workspaceId}
                        />
                    )}
                    {/*viewSelected === TEMPLATE_NAVIGATION_MENUS.BACKGROUND.value && (
                        <BackgroundComponent selectedTemplate={selectedTemplate} setSelectedTemplate={setSelectedTemplate} />
                    )*/}
                    {viewSelected === TEMPLATE_NAVIGATION_MENUS.FRAME.value && (
                        <FrameComponent selectedTemplate={selectedTemplate} setSelectedTemplate={setSelectedTemplate} />
                    )}
                    {viewSelected === TEMPLATE_NAVIGATION_MENUS.LOGO.value && (
                        <LogoComponent selectedTemplate={selectedTemplate} setSelectedTemplate={setSelectedTemplate} workspaceId={workspaceId} />
                    )}
                </div>
            </div>
        </div>
    );
};
